import { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Sparkles, X } from 'lucide-react';
import { useChatStore } from '../../store/useChatStore';
import { useStore } from '../../store/useStore';
import { isWelcomeBackSnoozed, snoozeWelcomeBack } from '../../lib/welcomeBackSnooze';


/**
 * Bottom sheet offering a short AI check-in when the member has been away for a
 * while. Starting it opens the chat drawer with the welcome-back interview.
 */
export function WelcomeBackPrompt() {
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const welcomeBack = useChatStore((s) => s.welcomeBack);
  const checkWelcomeBack = useChatStore((s) => s.checkWelcomeBack);
  const startWelcomeBack = useChatStore((s) => s.startWelcomeBack);
  const dismissWelcomeBack = useChatStore((s) => s.dismissWelcomeBack);

  useEffect(() => {
    if (!currentHouseholdId || isWelcomeBackSnoozed(currentHouseholdId)) return;
    checkWelcomeBack();
  }, [currentHouseholdId, checkWelcomeBack]);

  const visible =
    !!welcomeBack &&
    welcomeBack.eligible &&
    welcomeBack.householdId === currentHouseholdId;

  const handleLater = () => {
    if (welcomeBack) snoozeWelcomeBack(welcomeBack.householdId);
    dismissWelcomeBack();
  };

  return (
    <AnimatePresence>
      {visible && welcomeBack && (
        <>
          <motion.div
            className="fixed inset-0 z-[60] bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleLater}
            aria-hidden="true"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-back-title"
            className="fixed inset-x-0 bottom-0 z-[61] mx-auto max-w-lg rounded-t-3xl bg-surface p-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] shadow-raised"
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
          >
            <button
              type="button"
              onClick={handleLater}
              aria-label="Close"
              className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-7 w-7" />
            </div>

            <h2 id="welcome-back-title" className="mb-2 text-2xl font-bold text-foreground">
              Welcome back!
            </h2>
            <p className="mb-6 text-muted-foreground">
              It's been {welcomeBack.daysAway} days. Life happens — let's get your home back on
              track with a quick 2-minute check-in. We'll clear the backlog and fit your routine to
              how things are now.
            </p>

            <button
              type="button"
              onClick={() => startWelcomeBack()}
              className="btn-primary mb-3 h-12 w-full rounded-2xl text-base"
            >
              Let's get back on track
            </button>
            <button
              type="button"
              onClick={handleLater}
              className="h-12 w-full rounded-2xl text-base font-medium text-muted-foreground hover:bg-surface-raised focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              Not now
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
